import React from "react";
import { programPaymenAcademy } from "../../../shared/services";
import { Resolver, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ProgramPaymentType } from "../../../types/dto/program";
import { message } from "antd";
import moment from "moment";
import {
  PAYMENT_CONCEPTS,
  isSupplementalPaymentConcept,
  resolvePaymentConcept,
} from "../../../shared/utils/academyPaymentConcepts";

export interface UseEditProgramPaymentProps {
  payment: ProgramPaymentType | null;
  setOpenModal: (val: boolean) => void;
  onSuccess?: () => void;
}

type EditPaymentFormValues = {
  amount: string;
  payment_date: string;
  payment_concept: string;
  program_payment_schedule_id?: string | null;
};

export default function useEditProgramPayment({
  payment,
  setOpenModal,
  onSuccess,
}: UseEditProgramPaymentProps) {
  const concept = payment
    ? resolvePaymentConcept(payment.payment_concept)
    : PAYMENT_CONCEPTS.CUOTA;

  const { control, handleSubmit: onSubmit, reset, watch } = useForm<EditPaymentFormValues>({
    defaultValues: {
      payment_concept: concept,
      program_payment_schedule_id: payment?.program_payment_schedule_id
        ? String(payment.program_payment_schedule_id)
        : null,
      amount: payment ? String(payment.amount) : "",
      payment_date: payment?.payment_date
        ? moment(payment.payment_date).format("YYYY-MM-DD")
        : "",
    },
    resolver: yupResolver(
      yup.object({
        payment_concept: yup.string().required("Campo requerido"),
        program_payment_schedule_id: yup.mixed().when("payment_concept", {
          is: PAYMENT_CONCEPTS.CUOTA,
          then: (schema) => schema.required("Campo requerido"),
          otherwise: (schema) => schema.notRequired().nullable(),
        }),
        amount: yup.string().required("Campo requerido"),
        payment_date: yup.string().required("Campo requerido"),
      })
    ) as unknown as Resolver<EditPaymentFormValues>,
  });

  React.useEffect(() => {
    if (!payment) return;

    reset({
      payment_concept: resolvePaymentConcept(payment.payment_concept),
      program_payment_schedule_id: payment.program_payment_schedule_id
        ? String(payment.program_payment_schedule_id)
        : null,
      amount: String(payment.amount),
      payment_date: moment(payment.payment_date).format("YYYY-MM-DD"),
    });
  }, [payment, reset]);

  const [loading, setLoading] = React.useState(false);

  const handleSubmit = onSubmit(async (data) => {
    if (loading || !payment) return;
    setLoading(true);

    try {
      const paymentConcept = data.payment_concept || PAYMENT_CONCEPTS.CUOTA;
      const isSupplemental = isSupplementalPaymentConcept(paymentConcept);

      await programPaymenAcademy.patch(payment.id, {
        amount: +data.amount,
        payment_concept: paymentConcept,
        program_payment_schedule_id: isSupplemental
          ? null
          : +data.program_payment_schedule_id!,
        payment_date: moment(data.payment_date).toISOString(),
      });

      message.success("Pago actualizado correctamente");
      setOpenModal(false);
      onSuccess?.();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Error al actualizar el pago";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  });

  return {
    control,
    loading,
    handleSubmit,
    watch,
  };
}
